import React from 'react';
import { renderBarcodeSvg } from '../../utils/barcode';
import { Box } from '@mui/material';
import {
  getPharmacyContactInfo,
  GSP_LABEL_INFO,
} from '../../utils/pharmacyInfo';
import { formatExpiryForDisplay } from '../../utils/expiryFormat';
import {
  AUX_HTML_METRICS,
  AUX_STICKER_COUNT,
  fitAuxWarningForHtml,
} from '../../utils/auxWarningLayout';
import { isRxTotalOnVialLabelEnabled } from '../../config/pharmacyFeatureFlags';

// Pharmacy contact info now lives in utils/pharmacyInfo.ts: the five GSP
// pharmacies keep their curated entries; every other pharmacy (e.g. Fillware
// sites) resolves from its pharmacy record (MongoDB pharmacy document) — no
// code change to onboard one. Throws on missing data — NO silent fallback
// (the old Mississauga default shipped wrong addresses to Burnaby patients).
// Re-exported under the old names for existing consumers.
export const PHARMACY_INFO = GSP_LABEL_INFO;
export const getPharmacyInfo = getPharmacyContactInfo;

/**
 * One auxiliary ("warning") label, as returned by
 * `GET /api/printing/label-data`. `code` / `source` are set on FDB-sourced
 * warnings (GP-1046) so the origin of a printed warning is traceable; Kroll's
 * own DrgAuxWarn rows carry neither.
 */
export interface AuxiliaryWarningItem {
  description: string;
  sortOrder: number;
  /** FDB label_warning_code — absent for Kroll-sourced warnings. */
  code?: number;
  source?: 'kroll' | 'fdb';
}

export interface ThermalLabelData {
  rxNumber: string;
  fillDate: string;
  patientName: string;
  drugDescription: string;
  directions: string;
  dispQty: number;
  quantity?: number;  // Backend returns this field
  /**
   * Prescriber as printed, INCLUDING the title Kroll's practitioner designation
   * dictates — "NP Salvano, Lorraine" / "RPh Chau, Kevin" / "Dr. Chow, Andrew"
   * (GP-573). Renderers must not add a title of their own.
   */
  doctorName: string;
  /**
   * Explicit prescriber title override for formatPrescriberName (release 2138
   * review deploy shim): `undefined` = backend didn't resolve one (old backend
   * — renderer falls back to the pre-existing "Dr." prefix), `''` = backend
   * said no title / already embedded in doctorName, `'NP'` etc. = prefix it.
   * TODO(remove after 2.3.517 backend is live)
   */
  prescriberTitle?: string;
  pharmacyName: string;
  pharmacyId?: string; // Used to look up pharmacy info
  pharmacyAddress?: string;
  pharmacyCity?: string;
  pharmacyProvince?: string;
  pharmacyPostalCode?: string;
  pharmacyPhone?: string;
  pharmacyEmail?: string;
  refills: string;
  magicNumber?: string;
  lotNumber?: string;
  expiryDate?: string;
  /** GP-331: Multiple lot/expiry entries for multi-lot orders */
  lotNumbers?: Array<{ lot: string; expiry: string }>;
  upc?: string;
  form?: string;
  strength?: string;
  manufacturer?: string;
  genericName?: string;
  din?: string;
  packIndex?: number;
  totalPacks?: number;
  /**
   * PRX-18: this label is label `index` of `count` in a fill split across
   * labels (the pharmacy system's per-drug rule, or a staff change of it). It
   * prints its own share `quantity` instead of the fill quantity (and instead
   * of a balance fill's "X of Y"), and "Label i of N" in place of the pack counter.
   */
  splitLabel?: { index: number; count: number; quantity: number };
  auxiliaryWarnings?: AuxiliaryWarningItem[];
  /** GP-665: quantity actually in the bag this fill (X in "X of Y") */
  amountSent?: number;
  /** GP-665: full prescribed quantity (Y in "X of Y") */
  prescribedQty?: number;
  /** GP-665: 'owing' = initial short fill, 'balance' = top-up fill for a prior partial */
  balanceIndicator?: 'owing' | 'balance';
  /**
   * GP-869: total charge for this fill (cost + markup + fee + mix fee + SSC fee),
   * straight from `GET /api/printing/label-data`. Printed on the vial label at
   * Manitoba only. `null`/absent means Kroll had no usable number — never 0.
   */
  rxTotal?: number | null;
}

/**
 * GP-665: derive the balance-owing quantity/indicator text for a vial label.
 * Only active when the indicator AND both quantities are present — a label is
 * never printed as partial on incomplete data (it falls back to the normal
 * full-quantity rendering).
 */
export const getBalanceLabelText = (
  data: Pick<ThermalLabelData, 'amountSent' | 'prescribedQty' | 'balanceIndicator'>,
): { qtyText: string | null; indicatorText: string | null } => {
  const { amountSent, prescribedQty, balanceIndicator } = data;
  if (!balanceIndicator || amountSent == null || prescribedQty == null) {
    return { qtyText: null, indicatorText: null };
  }
  return {
    qtyText: `${amountSent} of ${prescribedQty}`,
    indicatorText: balanceIndicator === 'balance' ? '(BALANCE)' : '(BALANCE OWING)',
  };
};

/**
 * GP-869: the "Rx Total: $x.xx" line for a vial label.
 *
 * The College of Pharmacists of Manitoba requires the prescription total on the
 * vial label, so the line renders for the Winnipeg site only (see
 * `config/pharmacyFeatureFlags.ts` — one place answers "which sites print it").
 * Every other province's label is byte-identical to before.
 *
 * Returns `null` where the requirement doesn't apply. THROWS where it does apply
 * but the total is missing: printing "$0.00" on a $4,829 specialty fill is a
 * wrong value on a patient-facing, regulated document, and this repo's rule is
 * to throw rather than silently substitute a default. Both print paths already
 * catch and surface generator errors (same contract as `getPharmacyInfo` above),
 * so the operator sees a failure instead of a non-compliant label.
 */
export const getRxTotalLabelText = (
  data: Pick<ThermalLabelData, 'pharmacyId' | 'rxNumber' | 'rxTotal'>,
): string | null => {
  if (!isRxTotalOnVialLabelEnabled(data.pharmacyId)) return null;

  const total = data.rxTotal;
  if (typeof total !== 'number' || !Number.isFinite(total)) {
    throw new Error(
      `Missing rxTotal for Rx# ${data.rxNumber || '<unknown>'} at pharmacyId="${data.pharmacyId ?? '<undefined>'}". ` +
      `Manitoba's College requires the Rx total on the vial label, so this label cannot be printed ` +
      `without it. Check that GET /api/printing/label-data returned rxTotal (Rx.Cost + Markup + Fee ` +
      `+ MixFee + SSCFee from Kroll).`,
    );
  }

  return `Rx Total: $${total.toFixed(2)}`;
};

interface ThermalLabelPrintProps {
  data: ThermalLabelData;
  onPrintComplete?: () => void;
}

const ThermalLabelPrint: React.FC<ThermalLabelPrintProps> = ({ data, onPrintComplete }) => {

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${days[date.getDay()]} ${date.getDate()}-${months[date.getMonth()]}-${date.getFullYear()}`;
  };

  // GP-331: Standardize expiry date format to DD/MM/YYYY (day if available).
  // GP-1322: the rule now lives in utils/expiryFormat so the browser-print HTML
  // below renders the identical string this preview shows.
  const formatExpiryDate = formatExpiryForDisplay;

  const handlePrint = () => {
    // Get the label HTML
    const labelContent = document.querySelector('.thermal-label-print')?.outerHTML;

    if (!labelContent) {
      alert('Label content not found');
      return;
    }

    // Open a new window with just the label
    const printWindow = window.open('', '_blank', 'width=600,height=400');
    if (!printWindow) {
      alert('Please allow pop-ups for this site');
      return;
    }

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Print Label - Rx ${data.rxNumber}</title>
          <style>
            @page {
              size: 4.6in 2.1in landscape;
              margin: 0;
            }

            @media print {
              @page {
                size: 4.6in 2.1in landscape;
                margin: 0;
              }
              html, body {
                width: 4.6in;
                height: 2.1in;
                margin: 0;
                padding: 0;
              }
            }

            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }

            html, body {
              margin: 0;
              padding: 0;
              width: 4.6in;
              height: 2.1in;
              overflow: hidden;
            }

            .thermal-label-print {
              width: 4.6in;
              height: 2.1in;
              padding: 0.12in 0.06in 0.04in 0.06in;
              background: white;
              font-family: Arial, sans-serif;
              position: relative;
              overflow: hidden;
            }

            .label-content {
              width: 3.0in;
              height: 100%;
              font-size: 8pt;
              line-height: 1.1;
              color: #000;
              display: flex;
              flex-direction: column;
            }

            .auxiliary-space {
              position: absolute;
              top: 0;
              right: 0;
              width: 1.5in;
              height: 100%;
              border-left: 1px dashed #ccc;
            }

            .label-header-line {
              display: flex;
              justify-content: space-between;
              margin-bottom: 0.03in;
              padding-bottom: 0.02in;
              border-bottom: 1.5px solid #000;
              font-size: 6pt;
              flex-shrink: 0;
            }

            .pharmacy-info {
              flex: 1;
            }

            .pharmacy-name {
              font-weight: bold;
              font-size: 8pt;
            }

            .pharmacy-address {
              font-size: 5pt;
              line-height: 1.1;
              white-space: nowrap;
            }

            .pharmacy-contact {
              text-align: right;
              font-size: 5pt;
            }

            .pharmacy-email {
              font-size: 5pt;
              white-space: nowrap;
            }

            .directions {
              max-width: 2.9in;
              line-height: 1.2;
              font-size: 7pt;
              font-weight: bold;
              max-height: 0.55in;
              margin-bottom: 0.15in;
              overflow: hidden;
              flex-shrink: 0;
              display: -webkit-box;
              -webkit-line-clamp: 6;
              -webkit-box-orient: vertical;
            }

            .pharmacist {
              font-size: 6pt;
              margin-top: 0.01in;
              margin-bottom: 0;
              flex-shrink: 0;
            }

            /* GP-869: Manitoba-only Rx total line */
            .rx-total {
              font-weight: bold;
              font-size: 7pt;
              white-space: nowrap;
              flex-shrink: 0;
            }

            .lot-exp-line {
              display: flex;
              align-items: baseline;
              gap: 0.1in;
              margin-top: auto;
              flex-shrink: 0;
            }

            .expiry-date-label {
              font-size: 8pt;
              font-weight: bold;
              background-color: #ffff00;
              padding: 0.01in 0.04in;
              border-radius: 0.02in;
            }

            .date-refills {
              font-size: 7pt;
              margin-top: 0.01in;
              margin-bottom: 0;
            }

            .refills-badge {
              background-color: #ffff00;
              padding: 0.02in 0.05in;
              font-weight: bold;
              font-size: 7pt;
              border-radius: 0.02in;
              display: inline-block;
              margin-left: 0.08in;
            }

            .barcode-bottom {
              margin-top: auto;
              display: block;
              clear: both;
              width: 2.9in;
            }

            .barcode-bottom svg {
              width: 2.9in !important;
              height: 0.5in !important;
              display: block;
            }
          </style>
        </head>
        <body>
          ${labelContent}
        </body>
      </html>
    `);

    printWindow.document.close();

    // Wait for content to load, then print
    setTimeout(() => {
      printWindow.print();
      setTimeout(() => {
        printWindow.close();
        if (onPrintComplete) {
          onPrintComplete();
        }
      }, 500);
    }, 250);
  };

  // Get pharmacy info based on pharmacyId
  const pharmacyInfo = getPharmacyInfo(data.pharmacyId);

  // GP-665: partial/balance fills print "X of Y" plus a bold indicator line
  const { qtyText: balanceQtyText, indicatorText: balanceIndicatorText } = getBalanceLabelText(data);

  // GP-869: Manitoba College requirement — the Rx total on the vial label
  const rxTotalText = getRxTotalLabelText(data);

  return (
    <Box>
      {/* Hidden element for printing only */}
      <div className="thermal-label-print" style={{ display: 'none' }}>
        <div className="label-content">
          <div className="label-header-line">
            <div className="pharmacy-info">
              <div className="pharmacy-name">{pharmacyInfo.name} {pharmacyInfo.phone}</div>
              <div className="pharmacy-address">{pharmacyInfo.address} {pharmacyInfo.city},{pharmacyInfo.province} {pharmacyInfo.postalCode} {pharmacyInfo.email}</div>
            </div>
          </div>

          <div className="rx-main-section">
            <div className="rx-number-section">
              <span className="rx-label">Rx:</span>
              <span className="rx-number">{data.rxNumber}</span>
            </div>
            <div className="patient-name">{data.patientName}</div>
            <div className="drug-name">{data.drugDescription}</div>
            {data.dispQty && (
              <div className="dosing-qty">{balanceQtyText ?? data.dispQty} units</div>
            )}
            {balanceIndicatorText && (
              <div className="dosing-qty">{balanceIndicatorText}</div>
            )}
            {rxTotalText && (
              <div className="rx-total">{rxTotalText}</div>
            )}
            <div className="directions">{data.directions}</div>
            {data.doctorName && (
              <div className="pharmacist">{data.doctorName}</div>
            )}
            {/* GP-391: Expiry date highlighted with yellow background */}
            {data.lotNumbers && data.lotNumbers.length > 0 ? (
              data.lotNumbers.map((entry: { lot: string; expiry: string }, i: number) => (
                entry.expiry ? (
                  <div key={i} className="pharmacist">
                    <span className="expiry-date-label">Exp: {formatExpiryDate(entry.expiry)}</span>
                  </div>
                ) : null
              ))
            ) : (
              <>
                {data.expiryDate && (
                  <div className="pharmacist">
                    <span className="expiry-date-label">Exp: {formatExpiryDate(data.expiryDate)}</span>
                  </div>
                )}
              </>
            )}
            <div className="date-refills">
              {data.fillDate && formatDate(data.fillDate)}
              {data.refills && (
                <span className="refills-badge">Refills: {data.refills}</span>
              )}
            </div>
            <div className="barcode-bottom">
              <svg id="rx-barcode-preview"></svg>
            </div>
          </div>
        </div>
        <div className="auxiliary-space"></div>
      </div>

      {/* Exposed print method via ref or prop */}
      <button onClick={handlePrint} style={{ display: 'none' }} id="thermal-print-trigger">
        Print
      </button>
    </Box>
  );
};

export default ThermalLabelPrint;

// Export method to trigger print externally
export const printThermalLabel = (data: ThermalLabelData) => {
  // Get pharmacy info based on pharmacyId
  const pharmacyInfo = getPharmacyInfo(data.pharmacyId);

  // Generate barcode
  const barcodeText = data.magicNumber
    ? `KX~${data.rxNumber}|${data.magicNumber}`
    : data.rxNumber;

  // PRX-39: rendered here from the bundled jsbarcode and inlined into the
  // popup (it used to be drawn by a CDN script inside the popup, which
  // printed a barcode-less label whenever the script lost the race).
  // GP-982: CODE128 needs a quiet zone of at least 10 modules on each side
  // of the symbol or a scanner can't find its start/stop pattern. This drew
  // "margin: 0", so the bars ran to the edge of a left-aligned 2.9in box with
  // only the label's 0.06in padding standing in for the quiet zone — under
  // two modules' worth. marginLeft/marginRight reserve a full 10 modules; the
  // narrower module (2 instead of 2.5) pays for them, and still leaves the
  // bars twice as wide as the ZPL label's ^BY2. Top/bottom stay 0 so the bars
  // keep their height.
  const rxBarcodeSvg = renderBarcodeSvg(data.rxNumber, {
    width: 2,
    height: 40,
    displayValue: true,
    fontSize: 10,
    margin: 0,
    marginLeft: 20,
    marginRight: 20,
  });

  // Format date
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${days[date.getDay()]} ${date.getDate()}-${months[date.getMonth()]}-${date.getFullYear()}`;
  };

  // Pack numbering text
  // PRX-18: a split label shows "Label i of N" instead of the pack counter.
  const packInfo = data.splitLabel
    ? (data.splitLabel.count > 1 ? `<span class="pack-number">Label ${data.splitLabel.index} of ${data.splitLabel.count}</span>` : '')
    : (data.packIndex !== undefined && data.totalPacks !== undefined && data.totalPacks > 1)
      ? `<span class="pack-number">${data.packIndex} of ${data.totalPacks}</span>`
      : '';

  // GP-665: partial/balance fills print "X of Y" plus a bold indicator line.
  // PRX-18: a split label prints its own share instead.
  const balanceText = getBalanceLabelText(data);
  const balanceQtyText = data.splitLabel ? String(data.splitLabel.quantity) : balanceText.qtyText;
  const balanceIndicatorText = balanceText.indicatorText;

  // GP-1322: mirror the on-screen preview exactly — it formats the expiry
  // (GP-331) and renders one line per lot for a multi-lot pack (GP-1007), while
  // this browser-print HTML used to interpolate the raw `expiryDate` and ignore
  // `lotNumbers`. A reprint supplies the persisted ISO value, so the raw
  // interpolation printed "2027-03-01" on the product where the preview showed
  // "03/2027", and a multi-lot drug printed only one of its expiries.
  const expiryLinesHtml = (
    data.lotNumbers && data.lotNumbers.length > 0
      ? data.lotNumbers.map((entry) => entry.expiry || '')
      : [data.expiryDate || '']
  )
    .map((e) => formatExpiryForDisplay(e))
    .filter((e) => e.length > 0)
    .map((e) => `<span class="expiry-date-label">Exp: ${e}</span>`)
    .join('');

  // GP-1046: one warning per sticker, each sized to the largest font at which
  // its FULL text fits its sticker (see utils/auxWarningLayout). The old markup
  // used one fixed 6.5pt size for every warning, so long warnings overflowed
  // and were clipped by the strip's `overflow: hidden` — the "cut off" /
  // "bleeding into each other" reports from the dispensary focus group.
  const auxWarningsHTML = (data.auxiliaryWarnings || [])
    .slice(0, AUX_STICKER_COUNT)
    .map((w) => {
      const fit = fitAuxWarningForHtml(w.description);
      // The browser label reserves a 1.5in strip — about half the writing width
      // the Zebra layout gives the same sticker — so the longest ~8 % of FDB
      // warnings (>150 chars) still cannot fit it at a printable size. Those get
      // an explicit ellipsis rather than a silently amputated sentence, so the
      // reader can tell there is more and check the monograph. On the Zebra path
      // (the production printer) every warning fits in full.
      const text = fit.fits ? w.description : `${fit.lines.join(' ')}…`;
      return `<div class="aux-warning" style="font-size:${fit.font}pt;line-height:${AUX_HTML_METRICS.lineHeightRatio}">${text}</div>`;
    })
    .join('');
  // GP-869: Manitoba College requirement — the Rx total on the vial label.
  // Throws (rather than printing $0.00) if the total is missing at an MB site.
  const rxTotalText = getRxTotalLabelText(data);

  // Create label HTML directly
  const labelHTML = `
    <div class="thermal-label-print">
      <div class="label-content">
        <div class="label-header-line">
          <div class="pharmacy-info">
            <div class="header-row">
              <div class="pharmacy-name">${pharmacyInfo.name}</div>
              <div class="pharmacy-phone">${pharmacyInfo.phone}</div>
            </div>
            <div class="header-row">
              <div class="pharmacy-address">${pharmacyInfo.address} ${pharmacyInfo.city},${pharmacyInfo.province} ${pharmacyInfo.postalCode}</div>
              <div class="pharmacy-email">${pharmacyInfo.email}</div>
            </div>
          </div>
        </div>
        <div class="rx-main-section">
          <div class="rx-line">
            <div class="rx-number-section">
              <span class="rx-label">Rx:</span>
              <span class="rx-number">${data.rxNumber}</span>
            </div>
            
            <div class="rx-meta-section">
              <span class="rx-date">${data.fillDate ? formatDate(data.fillDate) : ''}</span>
              ${data.refills ? `<span class="refills-badge">Refills: ${data.refills}</span>` : ''}
            </div>
          </div>

          <div class="patient-name">${data.patientName}</div>
          <div class="drug-name">${data.drugDescription}</div>
          ${data.genericName ? `<div class="generic-name">${data.genericName} ${data.strength || ''}</div>` : ''}
          
          <div class="dosing-doctor-row">
            ${balanceQtyText || data.quantity || data.dispQty ? `<div class="dosing-qty">${balanceQtyText ?? (data.quantity || data.dispQty)}${data.form ? ' ' + data.form.split(' ')[0].split('(')[0] : ''}${data.manufacturer ? ' ' + data.manufacturer.split(' ')[0].split('(')[0] : ''}${data.din ? ' DIN: ' + data.din : ''}</div>` : data.din ? `<div class="dosing-qty">DIN: ${data.din}</div>` : '<div></div>'}
            ${rxTotalText ? `<div class="rx-total">${rxTotalText}</div>` : ''}
            ${data.doctorName ? `<div class="pharmacist">${data.doctorName}</div>` : ''}
          </div>
          ${balanceIndicatorText ? `<div class="dosing-qty">${balanceIndicatorText}</div>` : ''}

          <div class="directions">${data.directions}</div>

          <div class="lot-exp-line">
            ${expiryLinesHtml}
            ${packInfo}
          </div>

          <div class="barcode-container">
            <div class="barcode-bottom">
              ${rxBarcodeSvg}
            </div>
          </div>
        </div>
      </div>
      <div class="auxiliary-space">
        ${auxWarningsHTML}
      </div>
    </div>
  `;

  // Open print window
  const printWindow = window.open('', '_blank', 'width=600,height=400');
  if (!printWindow) {
    alert('Please allow pop-ups for this site');
    return;
  }

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Print Label - Rx ${data.rxNumber}</title>
        <style>
          @page {
            size: 4.6in 2.1in landscape;
            margin: 0;
          }
          @media print {
            @page {
              size: 4.6in 2.1in landscape;
              margin: 0;
            }
            html, body {
              width: 4.6in;
              height: 2.1in;
              margin: 0;
              padding: 0;
            }
          }
          * { margin: 0; padding: 0; box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          html, body { margin: 0; padding: 0; width: 4.6in; height: 2.1in; overflow: hidden; }
          .thermal-label-print { width: 4.6in; height: 2.1in; padding: 0.12in 0.06in 0.04in 0.06in; background: white; font-family: Arial, sans-serif; position: relative; overflow: hidden; }
          .label-content { width: 3.0in; height: 100%; font-size: 8pt; line-height: 1.1; color: #000; display: flex; flex-direction: column; }
          .auxiliary-space { position: absolute; top: 0; right: 0; width: 1.5in; height: 100%; border-left: 1px dashed #ccc; padding: 0.04in 0.06in; display: flex; flex-direction: column; overflow: hidden; }
          /* GP-1046: font-size/line-height are set per warning inline (see
             auxWarningsHTML) — the values here are only a fallback. The 0.02in
             block padding is the sticker gutter that keeps ink off the
             perforation, and align-items:center splits any slack evenly. */
          .aux-warning { font-size: 6.5pt; font-weight: bold; line-height: 1.15; flex: 1 1 0; min-height: 0; max-width: 1.38in; display: flex; align-items: center; word-wrap: break-word; overflow-wrap: break-word; word-break: break-word; white-space: normal; overflow: hidden; padding: 0.02in 0; }

          .label-header-line { margin-bottom: 0.03in; padding-bottom: 0.02in; border-bottom: 1.5px solid #000; font-size: 6pt; flex-shrink: 0; }
          .pharmacy-info { width: 100%; }
          .header-row { display: flex; justify-content: space-between; align-items: baseline; width: 100%; margin-bottom: 0.01in; }
          .pharmacy-name { font-weight: bold; font-size: 8pt; }
          .pharmacy-phone { font-size: 8pt; font-weight: bold; }
          .pharmacy-address { font-size: 5pt; line-height: 1.1; white-space: nowrap; }
          .pharmacy-email { font-size: 5pt; white-space: nowrap; }

          .rx-main-section { flex: 1; display: flex; flex-direction: column; overflow: hidden; }
          .rx-line { display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.02in; flex-shrink: 0; }
          .rx-number-section { background-color: #ffff00; padding: 0.02in 0.04in; display: inline-block; }
          .rx-label { font-weight: bold; font-size: 8pt; }
          .rx-number { font-weight: bold; font-size: 14pt; margin-left: 0.03in; }

          .rx-meta-section { display: flex; align-items: center; }
          .rx-date { font-size: 7pt; font-weight: bold; margin-right: 0.1in; }
          .refills-badge { background-color: #ffff00; padding: 0.02in 0.05in; font-weight: bold; font-size: 7pt; border-radius: 0.02in; }

          .patient-name { font-weight: bold; font-size: 10pt; margin-bottom: 0.02in; flex-shrink: 0; }
          .drug-name { font-weight: bold; font-size: 8pt; margin-bottom: 0.015in; flex-shrink: 0; }
          .generic-name { font-style: italic; font-size: 7pt; margin-bottom: 0.015in; flex-shrink: 0; }

          .dosing-doctor-row { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 0.015in; width: 2.9in; flex-shrink: 0; }
          .dosing-qty { font-weight: bold; font-size: 7pt; }
          /* GP-869: Manitoba-only Rx total, centred between the qty and the prescriber */
          .rx-total { font-weight: bold; font-size: 7pt; white-space: nowrap; }

          .directions { font-weight: bold; font-size: 7pt; margin-bottom: 0.15in; max-width: 2.9in; line-height: 1.2; word-wrap: break-word; overflow-wrap: break-word; max-height: 0.55in; overflow: hidden; flex-shrink: 0; display: -webkit-box; -webkit-line-clamp: 4; -webkit-box-orient: vertical; }
          .pharmacist { font-size: 6pt; margin: 0; flex-shrink: 0; }

          .lot-exp-line {
            display: flex;
            align-items: baseline;
            gap: 0.1in;
            margin-top: auto;
            width: 2.9in;
            flex-shrink: 0;
          }

          .expiry-date-label {
            font-size: 8pt;
            font-weight: bold;
            background-color: #ffff00;
            padding: 0.01in 0.04in;
            border-radius: 0.02in;
          }

          .barcode-container {
            margin-top: auto;
            padding-top: 0.02in;
            width: 2.9in;
            flex-shrink: 0;
          }
          .pack-number {
            font-size: 8pt;
            font-weight: bold;
          }
          .barcode-bottom {
            display: block;
            width: 100%;
            text-align: left;
          }
          .barcode-bottom svg { width: 100% !important; height: 0.5in !important; display: block; }
        </style>
      </head>
      <body>
        ${labelHTML}
        <script>
          // PRX-39: the barcode is already inline SVG in the markup above —
          // nothing to load, nothing to race. Print once the document is ready.
          setTimeout(function() {
            window.print();
            setTimeout(function() { window.close(); }, 500);
          }, 250);
        </script>
      </body>
    </html>
  `);

  printWindow.document.close();
};
